import { Box, MenuItem, Paper, Select, Stack, Typography, useTheme } from "@mui/material";
import KeyboardArrowRightRounded from "@mui/icons-material/KeyboardArrowRightRounded";
import CheckCircleOutlineRounded from "@mui/icons-material/CheckCircleOutlineRounded";
import PictureAsPdfRounded from "@mui/icons-material/PictureAsPdfRounded";
import { useState } from "react";
import apiService from "../../../../../../../service/Api";
import { showErrorToast, showSuccessToast } from "../../../../../../../components/common/ShowToast";
import { tokens } from "../../../../../../../theme";

type ProposalStatus = "Accepted" | "Sent" | "Declined" | "Final" | "Draft";

interface ProposalStatusSidebarProps {
  status: ProposalStatus;
  proposalId: number;
  onStatusChange?: (newStatus: ProposalStatus) => void;
  onGenerateInvoice?: () => void;
  onConfirmBooking?: () => void;
}

export default function ProposalStatusSidebar({
  status,
  proposalId,
  onStatusChange,
  onGenerateInvoice,
  onConfirmBooking,
}: ProposalStatusSidebarProps) {
  
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [currentStatus, setCurrentStatus] = useState<ProposalStatus>(status);
  const ENABLE_ACTIONS = false;
  const handleStatusChange = async (newStatus: ProposalStatus) => {
    
    setCurrentStatus(newStatus);
    try {
      await apiService.patch(`/api/bookings/booking-proposal/${proposalId}/`, {
        status: newStatus.toLowerCase(),
      });
      onStatusChange?.(newStatus);
      showSuccessToast(`Status updated to ${newStatus}`);
    } catch {
      showErrorToast("Failed to update status.");
      setCurrentStatus(currentStatus);
    }
  };
  return (
    <Stack spacing={2}>
      {/* Status dropdown */}
      <Paper sx={{ borderRadius: "14px", p: 2, boxShadow: 0 }}>
        <Typography
          sx={{
            fontWeight: 600,
            fontSize: "13px",
            color: colors.primary[100],
            mb: 1.5,
          }}
        >
          Status
        </Typography>
        <Select
          value={currentStatus}
          onChange={(e) => handleStatusChange(e.target.value as ProposalStatus)}
          fullWidth
          size="small"
          sx={{
            borderRadius: "10px",
            fontSize: "13px",
            fontWeight: 600,
            bgcolor: "background.paper",
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: colors.primary[800],
            },
          }}
        >
          <MenuItem value="Accepted">Accepted</MenuItem>
          <MenuItem value="Sent">Sent</MenuItem>
          <MenuItem value="Declined">Declined</MenuItem>
          <MenuItem value="Final">Final</MenuItem>
          <MenuItem value="Draft">Draft</MenuItem>
        </Select>

        {currentStatus === "Accepted" && (
          <Box
            sx={{
              mt: 1.5,
              bgcolor: "#f0fdf4",
              border: "1px solid #bbf7d0",
              borderRadius: "10px",
              p: 1.5,
            }}
          >
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{ mb: 0.5 }}
            >
              <CheckCircleOutlineRounded
                sx={{ fontSize: 16, color: "#16a34a" }}
              />
              <Typography
                sx={{ fontWeight: 700, fontSize: "12px", color: "#16a34a" }}
              >
                Proposal Accepted!
              </Typography>
            </Stack>
            <Typography
              sx={{
                fontSize: "11px",
                color: colors.primary[100],
                lineHeight: 1.5,
              }}
            >
              This proposal has been accepted by the client and is now locked.
            </Typography>
          </Box>
        )}

        {currentStatus === "Sent" && (
          <Box
            sx={{
              mt: 1.5,
              bgcolor: "#eff6ff",
              border: "1px solid #bfdbfe",
              borderRadius: "10px",
              p: 1.5,
            }}
          >
            <Typography
              sx={{ fontWeight: 700, fontSize: "12px", color: "#2563eb" }}
            >
              Awaiting Response
            </Typography>
            <Typography
              sx={{
                fontSize: "11px",
                color: "text.secondary",
                lineHeight: 1.5,
              }}
            >
              This proposal has been sent to the client.
            </Typography>
          </Box>
        )}

        {currentStatus === "Declined" && (
          <Box
            sx={{
              mt: 1.5,
              bgcolor: "#fef2f2",
              border: "1px solid #fecaca",
              borderRadius: "10px",
              p: 1.5,
            }}
          >
            <Typography
              sx={{ fontWeight: 700, fontSize: "12px", color: "#dc2626" }}
            >
              Proposal Declined
            </Typography>
            <Typography
              sx={{
                fontSize: "11px",
                color: colors.primary[800],
                lineHeight: 1.5,
              }}
            >
              The client has declined this proposal.
            </Typography>
          </Box>
        )}

        {currentStatus === "Final" && (
          <Box
            sx={{
              mt: 1.5,
              bgcolor: "#f0fdf4",
              border: "1px solid #bbf7d0",
              borderRadius: "10px",
              p: 1.5,
            }}
          >
            <Typography
              sx={{ fontWeight: 700, fontSize: "12px", color: "#16a34a" }}
            >
              Final Proposal
            </Typography>
            <Typography
              sx={{
                fontSize: "11px",
                color: "text.secondary",
                lineHeight: 1.5,
              }}
            >
              Proposal Ready!!
            </Typography>
          </Box>
        )}

        {currentStatus === "Draft" && (
          <Box
            sx={{
              mt: 1.5,
              bgcolor: "background.paper",
              border: `1px solid ${colors.primary[800]}`,
              borderRadius: "10px",
              p: 1.5,
            }}
          >
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: "12px",
                color: "text.secondary",
              }}
            >
              Draft
            </Typography>
            <Typography
              sx={{
                fontSize: "11px",
                color: "text.secondary",
                lineHeight: 1.5,
              }}
            >
              This proposal has not been sent yet.
            </Typography>
          </Box>
        )}
      </Paper>

      {/* Generate Invoice */}
      {ENABLE_ACTIONS && (
        <Paper
          onClick={onGenerateInvoice}
          sx={{
            borderRadius: "14px",
            p: 2,
            boxShadow: 0,
            cursor: "pointer",
            border: `1px solid ${colors.primary[400]}`,
            "&:hover": { bgcolor: "background.paper" },
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: "50%",
                bgcolor: "#f0e6da",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <PictureAsPdfRounded
                sx={{ fontSize: 20, color: colors.primary[500] }}
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: "13px",
                  color: colors.primary[400],
                }}
              >
                Generate Invoice
              </Typography>
              <Typography sx={{ fontSize: "11px", color: "text.secondary" }}>
                Generate invoice from this proposal
              </Typography>
            </Box>
            <KeyboardArrowRightRounded sx={{ color: "text.secondary" }} />
          </Stack>
        </Paper>
      )}

      {/* Confirm Booking */}
      {ENABLE_ACTIONS && (
        <Paper
          onClick={onConfirmBooking}
          sx={{
            borderRadius: 3,
            p: 2,
            boxShadow: 0,
            cursor: "pointer",
            border: `1px solid ${colors.primary[500]}`,
            "&:hover": { bgcolor: "background.paper" },
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: "50%",
                bgcolor: "#f5f0e8",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <CheckCircleOutlineRounded
                sx={{ fontSize: 20, color: colors.primary[500] }}
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: "13px",
                  color: colors.primary[500],
                }}
              >
                Confirm booking
              </Typography>
              <Typography sx={{ fontSize: "11px", color: colors.primary[100] }}>
                This is alert the Ops team for upcoming stream
              </Typography>
            </Box>
            <KeyboardArrowRightRounded sx={{ color: "text.secondary" }} />
          </Stack>
        </Paper>
      )}
    </Stack>
  );
}
